import { useSyncExternalStore } from 'react'
import { inbox } from './api'
import { store, type Conversation } from './store'

/**
 * Search across every account. Matches in the inbox you already have show
 * instantly; the server's cached search (archived and older mail included)
 * fills in a moment later.
 */
interface SearchState {
  query: string; results: Conversation[] | null; loading: boolean
  /** Ask (Jev + AI): plain-English results and what they show. */
  ai: Conversation[] | null; aiSummary: string; aiLoading: boolean
  /** Deep: every provider's own search (full history), ranked by Jev. Score is 0–1, null when unranked. */
  deep: Array<{ item: Conversation; score: number | null }> | null; deepLoading: boolean; deepSummary: string
}
let state: SearchState = { query: '', results: null, loading: false, ai: null, aiSummary: '', aiLoading: false, deep: null, deepLoading: false, deepSummary: '' }
let askEnabled = false
export const setAskEnabled = (on: boolean) => { askEnabled = on }
const listeners = new Set<() => void>()
const set = (next: Partial<SearchState>) => { state = { ...state, ...next }; for (const listener of listeners) listener() }
export const useSearch = () => useSyncExternalStore(listener => { listeners.add(listener); return () => listeners.delete(listener) }, () => state)
export const searchState = () => state

const words = (text: string) => text.toLowerCase().split(/\s+/).filter(Boolean)
function localMatches(query: string): Conversation[] {
  const terms = words(query)
  return store.get().conversations.filter(item => {
    const hay = `${item.lead.from.name} ${item.lead.from.email} ${item.subject} ${item.lead.preview}`.toLowerCase()
    return terms.every(term => hay.includes(term))
  })
}

let timer = 0
let generation = 0
let askTimer = 0
let askController: AbortController | null = null
let deepTimer = 0
let deepController: AbortController | null = null
/** Worth asking the model: several words, or a question. Single keywords stay instant. */
const naturalLanguage = (query: string) => words(query).length >= 2 || /\?$/.test(query)

export function setQuery(query: string) {
  const trimmed = query.trim()
  clearTimeout(timer); clearTimeout(askTimer); clearTimeout(deepTimer); askController?.abort(); deepController?.abort()
  if (!trimmed) { generation++; set({ query, results: null, loading: false, ai: null, aiSummary: '', aiLoading: false, deep: null, deepLoading: false, deepSummary: '' }); return }
  if (trimmed.length >= 2) {
    set({ deepLoading: true, ...(state.deep && state.query.trim() !== trimmed ? { deep: null } : {}) })
    const mine = generation + 1
    deepTimer = window.setTimeout(() => void runDeep(trimmed, mine, naturalLanguage(trimmed)), 380)
  }
  const ask = askEnabled && naturalLanguage(trimmed)
  if (ask) {
    set({ aiLoading: true })
    const mine = generation + 1
    askTimer = window.setTimeout(() => void runAsk(trimmed, mine), 320)
  } else set({ ai: null, aiSummary: '', aiLoading: false })
  set({ query, results: merge(localMatches(trimmed), state.query.trim() && state.results ? state.results.filter(item => !store.get().conversations.some(c => c.key === item.key)) : []), loading: true })
  const mine = ++generation
  timer = window.setTimeout(async () => {
    const remote = await searchServer(trimmed).catch(() => [])
    if (mine !== generation) return
    set({ results: merge(localMatches(trimmed), remote), loading: false })
  }, 110)
}

const localDay = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${now.toLocaleDateString('en-US', { weekday: 'long' })}`
}

async function runDeep(query: string, mine: number, natural: boolean) {
  const controller = new AbortController()
  deepController = controller
  try {
    const response = await fetch('/api/search/deep', { method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ q: query, today: localDay(), natural: natural && askEnabled }) })
    const { summary, results } = await response.json() as { summary: string; results: Array<{ sourceId: string; threadId: string; score: number | null }> }
    let ranked: Array<{ item: Conversation; score: number | null }> = []
    if (results.length) {
      const mailboxIds = store.get().mailboxes.slice(0, 50).map(m => m.id)
      const page = await inbox.mailboxConversations({ mailboxIds, keys: results.map(({ sourceId, threadId }) => ({ sourceId, threadId })), limit: results.length })
      const byKey = new Map(page.items.map(item => [`${item.sourceId}:${item.threadId}`, store.wrap(item)]))
      ranked = results.flatMap(r => { const item = byKey.get(`${r.sourceId}:${r.threadId}`); return item ? [{ item, score: r.score }] : [] })
    }
    if (controller.signal.aborted || mine !== generation) return
    set({ deep: ranked, deepSummary: summary, deepLoading: false })
  } catch {
    if (!controller.signal.aborted) set({ deepLoading: false })
  }
}

async function runAsk(query: string, mine: number) {
  const controller = new AbortController()
  askController = controller
  try {
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${now.toLocaleDateString('en-US', { weekday: 'long' })}`
    const response = await fetch('/api/search', { method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ q: query, today }) })
    const { summary, keys } = await response.json() as { summary: string; keys: Array<{ sourceId: string; threadId: string }> }
    let found: Conversation[] = []
    if (keys.length) {
      const mailboxIds = store.get().mailboxes.slice(0, 50).map(m => m.id)
      const page = await inbox.mailboxConversations({ mailboxIds, keys, limit: keys.length })
      found = page.items.map(item => store.wrap(item)).sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
    }
    if (controller.signal.aborted || mine !== generation) return
    set({ ai: found, aiSummary: summary, aiLoading: false })
  } catch {
    if (!controller.signal.aborted) set({ ai: null, aiSummary: '', aiLoading: false })
  }
}
export const clearSearch = () => setQuery('')

function merge(local: Conversation[], remote: Conversation[]) {
  const byKey = new Map<string, Conversation>()
  for (const item of [...local, ...remote]) if (!byKey.has(item.key)) byKey.set(item.key, item)
  return [...byKey.values()].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
}

async function searchServer(query: string): Promise<Conversation[]> {
  const mailboxIds = store.get().mailboxes.slice(0, 50).map(m => m.id)
  if (!mailboxIds.length) return []
  const page = await inbox.mailboxMessages({ mailboxIds, search: query.slice(0, 200), sort: 'newest', limit: 60 })
  const keys: Array<{ sourceId: string; threadId: string }> = []
  const seen = new Set<string>()
  for (const message of page.items) {
    if (['spam', 'trash', 'drafts'].includes(message.folder)) continue
    const key = `${message.sourceId}:${message.threadId}`
    if (seen.has(key)) continue
    seen.add(key); keys.push({ sourceId: message.sourceId, threadId: message.threadId })
    if (keys.length >= 30) break
  }
  if (!keys.length) return []
  const conversations = await inbox.mailboxConversations({ mailboxIds, keys, limit: keys.length })
  return conversations.items.map(item => store.wrap(item))
}
